import { supabase } from '../lib/supabase';

export type CablePricingSystem = 'AC' | 'DC';

export interface CablePriceRow {
  system: CablePricingSystem;
  /**
   * For AC rows, phases should be 1 or 3 when available.
   */
  phase?: 1 | 3;
  /**
   * Cable size in mm² (per core). For multicore specs like "4x16mm2", sizeMm2 is 16.
   */
  sizeMm2: number;
  /**
   * Price per meter in USD (matches bos_components.unit_price_usd).
   */
  unitPriceUsdPerM: number;
  /**
   * Optional labor per meter (matches bos_components.labor_hours).
   */
  laborHoursPerM?: number;
  /**
   * Optional labor rate in USD/hour (matches bos_components.labor_rate_usd).
   */
  laborRateUsdPerHour?: number;
  componentId: string;
  componentName: string;
}

export interface CablePricingTable {
  dc: CablePriceRow[];
  ac3: CablePriceRow[]; // 3-phase pricing (typically 4-core)
  ac1: CablePriceRow[]; // 1-phase pricing (optional; if empty we can derive from ac3)
  fetchedAt: string; // ISO
}

function safeNumber(n: unknown, fallback = 0): number {
  const v = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function parseSpec(spec: unknown): any {
  if (spec == null) return null;
  if (typeof spec === 'object') return spec as any;
  if (typeof spec === 'string') {
    try {
      return JSON.parse(spec);
    } catch {
      return null;
    }
  }
  return null;
}

function extractSizeMm2(spec: any): number | null {
  if (!spec) return null;

  // Preferred: store numeric size_mm2 in JSON for easy parsing
  const sizeMm2 = safeNumber(spec.size_mm2, NaN);
  if (Number.isFinite(sizeMm2) && sizeMm2 > 0) return sizeMm2;

  const size = typeof spec.size === 'string' ? spec.size : null;
  if (!size) return null;

  // Examples:
  // - "10mm2"
  // - "4x25mm2"
  // - "3x16mm²"
  const normalized = size.toLowerCase().replace('mm²', 'mm2').trim();
  const match = normalized.match(/(?:\d+\s*x\s*)?(\d+(?:\.\d+)?)\s*mm2/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function extractPhase(spec: any): 1 | 3 | undefined {
  if (!spec) return undefined;
  const p = safeNumber(spec.phases, NaN);
  if (p === 1 || p === 3) return p;
  return undefined;
}

let cached: CablePricingTable | null = null;
let cachedAtMs = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function fetchCablePricingTable(): Promise<CablePricingTable | null> {
  const now = Date.now();
  if (cached && now - cachedAtMs < CACHE_TTL_MS) return cached;

  const { data, error } = await supabase
    .from('bos_components')
    .select('id, category, subcategory, component_name, specification, unit, unit_price_usd, labor_hours, labor_rate_usd, availability_saudi')
    .eq('category', 'cable')
    .eq('availability_saudi', true)
    .eq('unit', 'per_meter');

  if (error || !data) return null;

  const dc: CablePriceRow[] = [];
  const ac3: CablePriceRow[] = [];
  const ac1: CablePriceRow[] = [];

  for (const row of data as any[]) {
    const spec = parseSpec(row.specification);
    const sizeMm2 = extractSizeMm2(spec);
    if (!sizeMm2) continue;

    const unitPriceUsdPerM = safeNumber(row.unit_price_usd, NaN);
    if (!Number.isFinite(unitPriceUsdPerM) || unitPriceUsdPerM <= 0) continue;

    const laborHoursPerM = safeNumber(row.labor_hours, NaN);
    const laborRateUsdPerHour = safeNumber(row.labor_rate_usd, NaN);

    const base: CablePriceRow = {
      system: 'DC',
      sizeMm2,
      unitPriceUsdPerM,
      laborHoursPerM: Number.isFinite(laborHoursPerM) && laborHoursPerM > 0 ? laborHoursPerM : undefined,
      laborRateUsdPerHour: Number.isFinite(laborRateUsdPerHour) && laborRateUsdPerHour > 0 ? laborRateUsdPerHour : undefined,
      componentId: String(row.id),
      componentName: String(row.component_name || ''),
    };

    const sub = String(row.subcategory || '').toLowerCase();
    if (sub.includes('dc')) {
      dc.push({ ...base, system: 'DC' });
      continue;
    }

    // AC rows
    const phase = extractPhase(spec);
    const acRow: CablePriceRow = { ...base, system: 'AC', phase: phase ?? undefined };

    if (phase === 1 || sub.includes('1ph') || sub.includes('single')) {
      ac1.push({ ...acRow, phase: 1 });
    } else {
      // default to 3-phase pricing (most existing rows are 3-phase)
      ac3.push({ ...acRow, phase: phase === 3 ? 3 : 3 });
    }
  }

  const sortBySize = (a: CablePriceRow, b: CablePriceRow) => a.sizeMm2 - b.sizeMm2;
  dc.sort(sortBySize);
  ac3.sort(sortBySize);
  ac1.sort(sortBySize);

  cached = {
    dc,
    ac3,
    ac1,
    fetchedAt: new Date().toISOString(),
  };
  cachedAtMs = now;
  return cached;
}



